/**
 * The global plan-cap modal (ADR-0041 §5/§11) — mounted once in `AppShell`. Opens when the
 * API client hits a 402 (`plan_limit_reached` / `plan_feature_locked`), turning a quota/
 * feature breach into a friendly cap/upsell dialog instead of a raw error toast.
 */
import { useTranslation } from "react-i18next";

import { featureLabel } from "@/components/billing/FeatureGate";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useCapModal } from "@/lib/capModal";

/** Human labels for the known limit axes (fallback: the raw metric key). */
const METRIC_LABELS: Record<string, string> = {
  max_executions_month: "esecuzioni/mese",
  max_storage_gb: "spazio (GB)",
  max_users: "utenti operatori",
  max_apps: "app",
  max_workflows: "workflow",
  max_etl_pipelines: "pipeline ETL",
};

export function CapModal() {
  const { t } = useTranslation();
  const detail = useCapModal((s) => s.detail);
  const close = useCapModal((s) => s.close);
  if (!detail) return null;

  const isFeature = detail.code === "plan_feature_locked";
  const title = isFeature
    ? t("billing.capFeatureTitle", "Funzionalità non nel piano")
    : t("billing.capLimitTitle", "Limite del piano raggiunto");
  const body = isFeature
    ? t("billing.capFeatureBody", {
        defaultValue: "“{{feature}}” non è disponibile nel piano attuale.",
        feature: featureLabel(detail.feature ?? ""),
      })
    : t("billing.capLimitBody", {
        defaultValue: "Hai raggiunto il limite di {{limit}} {{metric}} del tuo piano.",
        limit: detail.limit ?? "—",
        metric: METRIC_LABELS[detail.metric ?? ""] ?? detail.metric ?? "",
      });

  return (
    <Dialog open onOpenChange={(o) => !o && close()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm text-muted-foreground">{body}</p>
          <p className="mt-2 text-xs text-muted-foreground">
            {t(
              "billing.capContact",
              "Contatta l'amministratore della piattaforma per passare a un piano superiore.",
            )}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button onClick={close}>{t("common.close", "Chiudi")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
